import { useCallback, useEffect, useRef, useState } from 'react';
import {
  chooseVoiceCodec,
  elapsedSeconds,
  voiceCapReached,
  VOICE_MAX_BYTES,
  setVoiceCaptureActive,
} from './consoleVoice';
import {
  deleteVoiceDraft,
  loadVoiceDraft,
  saveVoiceDraft,
  voiceContext,
  type VoiceDraft,
} from './consoleVoiceDraft';
import type { HuddleRecognition } from './useHuddleDictation';

// Task dictation listens to this signal; it never stops the MediaRecorder.
export function useConsoleRecorder(token: string) {
  const [phase, setPhase] = useState<'idle' | 'starting' | 'recording' | 'review' | 'kept'>('idle');
  const [draft, setDraft] = useState<VoiceDraft | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [notice, setNotice] = useState('');
  const [persistence, setPersistence] = useState('');
  const [transcribe, setTranscribe] = useState(false);
  const [liveText, setLiveText] = useState('');
  const capture = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const recognition = useRef<HuddleRecognition | null>(null);
  const startAt = useRef(0);
  const stoppedAt = useRef<number | null>(null);
  const context = useRef('');
  const current = useRef<VoiceDraft | null>(null);
  const generation = useRef(0);
  const busy = useRef(false);
  const stopping = useRef(false);
  const interacted = useRef(false);
  const writes = useRef(Promise.resolve());
  const persist = useCallback((value: VoiceDraft) => {
    current.current = value;
    setDraft(value);
    writes.current = writes.current
      .then(() => saveVoiceDraft(value))
      .catch(() => setPersistence('Draft stays in this tab only.'));
  }, []);
  const release = useCallback(() => {
    recognition.current?.abort();
    recognition.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    setVoiceCaptureActive(false);
  }, []);
  const stop = useCallback((interrupted = false) => {
    const recorder = capture.current;
    if (!recorder || recorder.state === 'inactive' || stopping.current) return;
    stopping.current = true;
    stoppedAt.current = Date.now();
    setElapsed(elapsedSeconds(startAt.current, stoppedAt.current));
    if (interrupted) setNotice('Recording stopped when you left the page. Review what was saved.');
    recognition.current?.stop();
    try {
      recorder.requestData();
    } catch {
      /* Some engines already flushed on interruption. */
    }
    recorder.stop();
  }, []);
  useEffect(() => {
    const version = ++generation.current;
    context.current = '';
    interacted.current = false;
    current.current = null;
    busy.current = false;
    setDraft(null);
    setPhase('idle');
    void voiceContext(token)
      .then(async (id) => {
        if (version !== generation.current) return;
        context.current = id;
        const saved = await loadVoiceDraft(id);
        if (version === generation.current && saved && !interacted.current) {
          current.current = saved;
          setDraft(saved);
          setPhase('kept');
        }
      })
      .catch(() => setPersistence('Draft stays in this tab only.'));
    const hidden = () => {
      if (document.visibilityState === 'hidden') stop(true);
    };
    const pagehide = () => stop(true);
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('pagehide', pagehide);
    return () => {
      ++generation.current;
      if (capture.current?.state === 'recording') capture.current.stop();
      capture.current = null;
      release();
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('pagehide', pagehide);
    };
  }, [token, release, stop]);
  useEffect(() => {
    if (phase !== 'recording') return;
    const timer = window.setInterval(() => {
      const seconds = elapsedSeconds(startAt.current, Date.now());
      setElapsed(seconds);
      if (voiceCapReached(seconds, 0)) stop();
    }, 250);
    return () => clearInterval(timer);
  }, [phase, stop]);
  async function start() {
    if (busy.current || capture.current?.state === 'recording') return;
    interacted.current = true;
    busy.current = true;
    stopping.current = false;
    setPhase('starting');
    setNotice('');
    setLiveText('');
    const version = generation.current;
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined')
        throw Error('unsupported');
      // Stop any existing task Speak before requesting capture.
      setVoiceCaptureActive(true);
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (version !== generation.current) {
        media.getTracks().forEach((t) => t.stop());
        return;
      }
      stream.current = media;
      if (document.visibilityState === 'hidden') throw Error('interrupted');
      const mime = chooseVoiceCodec((type) => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(media, {
        ...(mime ? { mimeType: mime } : {}),
        audioBitsPerSecond: 64000,
      });
      const actual = recorder.mimeType.split(';')[0];
      if (actual !== 'audio/mp4' && actual !== 'audio/webm') throw Error('unsupported');
      capture.current = recorder;
      const parts: Blob[] = [];
      let bytes = 0;
      let partial = false;
      let words = '';
      let discarded = false;
      const value: VoiceDraft = {
        context: context.current || crypto.randomUUID(),
        id: crypto.randomUUID(),
        created: Date.now(),
        blob: new Blob([], { type: actual }),
        transcript: '',
        partial: false,
        duration: 0,
      };
      const snapshot = () => ({
        ...value,
        blob: new Blob(parts, { type: actual }),
        transcript: words,
        partial,
        duration: Math.max(
          0.1,
          Math.min(180, ((stoppedAt.current ?? Date.now()) - startAt.current) / 1000),
        ),
      });
      recorder.ondataavailable = (e) => {
        if (version !== generation.current || discarded || !e.data.size) return;
        if (bytes + e.data.size > VOICE_MAX_BYTES) {
          setNotice('Recording reached the size limit. Review what was saved.');
          stop();
          return;
        }
        parts.push(e.data);
        bytes += e.data.size;
        persist(snapshot());
        if (voiceCapReached(elapsedSeconds(startAt.current, Date.now()), bytes)) stop();
      };
      recorder.onstop = () => {
        if (version !== generation.current) return;
        if (current.current === null && discarded) return;
        capture.current = null;
        persist(snapshot());
        release();
        busy.current = false;
        setPhase('review');
      };
      recorder.onerror = () => {
        partial = true;
        stop(true);
      };
      media.getTracks().forEach((track) => {
        track.addEventListener('ended', () => stop(true));
        track.addEventListener('mute', () => stop(true));
      });
      // A canceled session ignores late final chunks and clears queued persistence.
      cancelActive.current = () => {
        discarded = true;
        recorder.onstop = null;
        recorder.ondataavailable = null;
        if (recorder.state !== 'inactive') recorder.stop();
        capture.current = null;
      };
      recorder.start(1000);
      stoppedAt.current = null;
      startAt.current = Date.now();
      setElapsed(0);
      setPhase('recording');
      current.current = value;
      setDraft(value);
      if (transcribe) {
        const speech = window as Window & {
          SpeechRecognition?: new () => HuddleRecognition;
          webkitSpeechRecognition?: new () => HuddleRecognition;
        };
        const Recognition = speech.SpeechRecognition ?? speech.webkitSpeechRecognition;
        if (Recognition) {
          const r = new Recognition();
          recognition.current = r;
          r.lang = 'en-US';
          r.continuous = true;
          r.interimResults = true;
          const finals = new Set<number>();
          r.onresult = (e) => {
            if (recognition.current !== r) return;
            let interim = '';
            for (let i = e.resultIndex; i < e.results.length; i++) {
              const result = e.results[i];
              if (!result) continue;
              if (result.isFinal && !finals.has(i)) {
                finals.add(i);
                words += (words ? ' ' : '') + result[0].transcript;
              } else if (!result.isFinal) interim += result[0].transcript;
            }
            setLiveText(words + (interim ? ' ' + interim : ''));
          };
          r.onerror = () => {
            partial = true;
          };
          r.onend = () => {
            if (capture.current?.state === 'recording') partial = true;
          };
          try {
            r.start();
          } catch {
            partial = true;
            recognition.current = null;
          }
        }
      }
    } catch (error) {
      if (version !== generation.current) return;
      release();
      capture.current = null;
      busy.current = false;
      setPhase('idle');
      setNotice(
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'Microphone blocked. Allow it for this site, or type a message.'
          : 'Recording unavailable. Try again, or type a message.',
      );
    }
  }
  const cancelActive = useRef<() => void>(() => {});
  function discard() {
    interacted.current = true;
    cancelActive.current();
    release();
    busy.current = false;
    const id = current.current?.context;
    current.current = null;
    setDraft(null);
    setPhase('idle');
    setNotice('');
    if (id)
      writes.current = writes.current
        .then(() => deleteVoiceDraft(id))
        .catch(() => setPersistence('Draft stays in this tab only.'));
  }
  function edit(transcript: string) {
    if (current.current) persist({ ...current.current, transcript });
  }
  return {
    phase,
    draft,
    elapsed,
    notice,
    persistence,
    transcribe,
    setTranscribe,
    liveText,
    start,
    stop,
    discard,
    edit,
    keep: () => setPhase('kept'),
    resume: () => setPhase('review'),
  };
}
